/**
 * Pseudonymous Amplitude user IDs.
 *
 * The Amplitude user id is ALWAYS hashed — regardless of consent — so a user
 * keeps a single, continuous profile across consent changes and across frontend
 * (now direct) and backend (via proxy) events.
 *
 * GROUND TRUTH: the consent proxy (Python AWS Lambda @ api.iu.org) has always
 * hashed both frontend and backend `user_id`s with the algorithm replicated
 * here. Because the frontend now bypasses the proxy (@amplitude/unified sends
 * events directly), it must reproduce that exact hash so existing profiles stay
 * intact — there is no cutoff and no "existing users appear as new" break.
 *
 * The proxy uses Python `uuid.UUID(bytes=sha256(...)[:16])`, which does NOT set
 * any version/variant bits — the raw first 16 SHA-256 bytes are hex-formatted
 * as-is. The result is intentionally NOT a spec-compliant UUIDv4; that is
 * irrelevant because Amplitude matches purely on the string.
 *
 * !!! DO NOT stamp UUIDv4 version/variant bits (byte[6] |= 0x40, byte[8] |= 0x80)
 * onto the digest. Doing so drifts the frontend away from the proxy/backend and
 * splits users into duplicate profiles. See docs/AMPLITUDE-USER-ID-HASHING.md.
 *
 * This hashing is the single source of truth for the frontend: the same hashed
 * id is used for Analytics, Session Replay and Experiment (set once via
 * `setUserId`).
 */

const formatDigestAsUuid = (digest: ArrayBuffer): string => {
  // Raw first 16 bytes, no version/variant manipulation — see file header.
  const bytes = new Uint8Array(digest).slice(0, 16)
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`
}

/**
 * Returns the pseudonymous (SHA-256, UUID-formatted) Amplitude user id for the
 * given raw UUID. The input is normalized (trimmed + lowercased) before hashing.
 */
const getHashedUserId = async (rawUserId: string): Promise<string> => {
  const normalized = rawUserId.trim().toLowerCase()
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(normalized),
  )
  return formatDigestAsUuid(digest)
}

export default { getHashedUserId }
